import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ordersService } from "api/services/orders.service";
import { searchService } from "api/services/search.service";
import { usersService } from "api/services/users.service";
import ContentHeader from "components/Layout/ContentHeader";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import { getApiErrorMessage } from "utils/api-error";
import {
  boundMemberSearchInput,
  decideMemberSearch,
  MEMBER_SEARCH_LABEL,
  MEMBER_SEARCH_LIMIT,
  MEMBER_SEARCH_INVALID_NOTICE,
  MEMBER_SEARCH_TOO_LONG_NOTICE,
  MEMBER_SEARCH_PHONE_NOTICE,
  MEMBER_SEARCH_TOO_SHORT_NOTICE,
} from "utils/member-search";

import type { NaverOrderProductType, NaverOrderRegisterRequest } from "api/types/order.types";
import type { SearchProductType, SearchResultItem } from "api/types/search.types";
import type { AdminUserListItem } from "api/types/user.types";

import "pages/AdminPage.scss";
import "./NaverOrderRegister.scss";

interface FormState {
  product_type: NaverOrderProductType;
  quantity: string;
  total_amount: string;
  naver_order_id: string;
  payment_date: string;
  note: string;
}

const PRODUCT_TYPE_OPTIONS: { value: NaverOrderProductType; label: string }[] = [
  { value: "PRODUCT", label: "제품" },
  { value: "LESSON", label: "강습" },
  { value: "TRAVEL", label: "투어" },
];

const PRODUCT_LABEL: Record<NaverOrderProductType, string> = {
  PRODUCT: "제품",
  LESSON: "강습",
  TRAVEL: "투어",
};

// 상품 유형(NaverOrderProductType) → 통합 검색 type 파라미터
const SEARCH_TYPE_MAP: Record<NaverOrderProductType, SearchProductType> = {
  PRODUCT: "product",
  LESSON: "lesson",
  TRAVEL: "travel",
};

const initialForm: FormState = {
  product_type: "PRODUCT",
  quantity: "1",
  total_amount: "",
  naver_order_id: "",
  payment_date: "",
  note: "",
};

const NaverOrderRegister: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // 회원 검색
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  // 회원 검색 전용 오류 — 폼 제출 오류(`error`)와 분리한다.
  const [userSearchError, setUserSearchError] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userSearchNotice, setUserSearchNotice] = useState("");
  const [selectedUser, setSelectedUser] = useState<AdminUserListItem | null>(null);

  // 상품 검색 (통합 검색 API)
  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState<SearchResultItem[]>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productSearched, setProductSearched] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<SearchResultItem | null>(null);

  // 회원 검색은 BE 에 위임한다 (디바운스 300ms).
  // 전 회원 선로딩은 `page_size` 상한(200) 때문에 성립하지 않는다 — 첫 페이지만
  // 받아놓고 클라이언트에서 거르면 그 밖의 회원이 조용히 검색되지 않는다.
  useEffect(() => {
    const decision = decideMemberSearch(userQuery);
    setUserSearchNotice(
      decision.kind === "phone-blocked"
        ? MEMBER_SEARCH_PHONE_NOTICE
        : decision.kind === "invalid-empty"
          ? MEMBER_SEARCH_INVALID_NOTICE
          : decision.kind === "too-long"
            ? MEMBER_SEARCH_TOO_LONG_NOTICE
            : decision.kind === "too-short"
              ? MEMBER_SEARCH_TOO_SHORT_NOTICE
              : "",
    );

    // 전화번호 형태·너무 짧은 입력은 **요청 자체를 만들지 않는다** —
    // 검색어는 GET 쿼리로 나가 로그·히스토리에 남는다.
    if (decision.kind !== "search") {
      setUsers([]);
      setUsersLoading(false);
      setUserSearchError("");
      return;
    }

    let cancelled = false;
    setUsersLoading(true);
    // 새 검색이 시작되면 이전 검색 오류를 즉시 해제한다 — 응답을 기다리며
    // 낡은 오류를 보여주지 않는다.
    setUserSearchError("");
    const timer = setTimeout(async () => {
      try {
        const res = await usersService.search(decision.term, { page_size: MEMBER_SEARCH_LIMIT });
        if (cancelled) return;
        setUsers(res.data.results);
        setUserSearchError("");
      } catch (err) {
        if (cancelled) return;
        setUsers([]);
        // 검색 실패는 **폼 제출 오류와 다른 상태**에 담는다. 같은 상태를 쓰면 서로를 덮고,
        // 후보 영역은 빈 배열만 보고 "일치하는 회원이 없습니다" 를 띄워 관리자가
        // 서버 장애를 "회원 없음" 으로 오해한다.
        setUserSearchError(
          getApiErrorMessage(err) || "회원 검색에 실패했습니다. 잠시 후 다시 시도하세요.",
        );
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userQuery]);

  // 검색어(q) + 상품 유형(type) 변경 시 통합 검색 (디바운스 300ms)
  useEffect(() => {
    const q = productQuery.trim();
    if (q.length < 2) {
      setProductResults([]);
      setProductSearched(false);
      setProductLoading(false);
      return;
    }

    let cancelled = false;
    setProductLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchService.search({
          q,
          type: SEARCH_TYPE_MAP[form.product_type],
        });
        if (cancelled) return;
        setProductResults(res.data.results);
        setProductSearched(true);
      } catch (err) {
        if (cancelled) return;
        setError(getApiErrorMessage(err));
        setProductResults([]);
        setProductSearched(true);
      } finally {
        if (!cancelled) setProductLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [productQuery, form.product_type]);

  // 후보는 서버가 이미 필터·상한 처리한 결과다. 방어적으로 상한만 한 번 더 건다.
  const candidates = useMemo(() => users.slice(0, MEMBER_SEARCH_LIMIT), [users]);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetProductSearch = () => {
    setProductQuery("");
    setProductResults([]);
    setProductSearched(false);
    setSelectedProduct(null);
  };

  const buildPayload = (): NaverOrderRegisterRequest | null => {
    if (!selectedUser) {
      setError("회원을 선택하세요.");
      return null;
    }
    if (!selectedProduct) {
      setError(`${PRODUCT_LABEL[form.product_type]}을(를) 검색 후 선택하세요.`);
      return null;
    }
    const productId = selectedProduct.id;
    if (!form.total_amount.trim()) {
      setError("결제 금액을 입력하세요.");
      return null;
    }
    const totalAmount = Number(form.total_amount);
    if (!Number.isFinite(totalAmount) || totalAmount < 0) {
      setError("결제 금액은 양수여야 합니다.");
      return null;
    }
    const quantity = Number(form.quantity || "1");
    if (!Number.isFinite(quantity) || quantity < 1) {
      setError("수량은 1 이상의 정수여야 합니다.");
      return null;
    }

    const payload: NaverOrderRegisterRequest = {
      user_id: selectedUser.user_id,
      product_type: form.product_type,
      quantity,
      total_amount: totalAmount,
    };
    if (form.product_type === "LESSON") payload.lesson_product_id = productId;
    else if (form.product_type === "TRAVEL") payload.travel_product_id = productId;
    else payload.product_id = productId;

    if (form.naver_order_id.trim()) payload.naver_order_id = form.naver_order_id.trim();
    if (form.payment_date) payload.payment_date = `${form.payment_date}:00`;
    if (form.note.trim()) payload.note = form.note.trim();

    return payload;
  };

  const handleReset = () => {
    setForm(initialForm);
    setSelectedUser(null);
    setUserQuery("");
    resetProductSearch();
    setError("");
  };

  const handleSubmit = async () => {
    setError("");
    const payload = buildPayload();
    if (!payload) return;

    setSubmitting(true);
    try {
      const res = await ordersService.registerNaverOrder(payload);
      const { order_id, ticket_id, message } = res.data;
      const lines = [message || "네이버 주문이 등록되었습니다.", `주문 ID: ${order_id}`];
      if (ticket_id) lines.push(`발급된 티켓 ID: ${ticket_id}`);
      alert(lines.join("\n"));
      // 주문 목록으로 이동 (등록된 주문이 자동 갱신되어 보이도록)
      // navigate("/order-management");
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-page">
      <ContentHeader
        title="네이버 주문 등록"
        description="네이버페이 / 스마트스토어 등 외부 결제 주문을 시스템에 사후 등록합니다."
        actions={[
          <Button key="back" variant="ghost" onClick={() => navigate("/order-management")}>
            ← 주문 목록
          </Button>,
        ]}
      />

      <div className="admin-page__card" style={{ padding: 20 }}>
        <div className="naver-order-page">
          <div className="naver-order-page__notice">
            네이버페이/스마트스토어 등 외부에서 결제된 주문을 시스템에 사후 등록합니다. 강습 상품의
            경우 등록과 동시에 티켓이 자동 발급됩니다.
          </div>

          <section className="naver-order-page__section">
            <div className="naver-order-page__section-title">회원</div>
            <Input
              label={MEMBER_SEARCH_LABEL}
              placeholder="검색어를 입력하세요"
              value={userQuery}
              onChange={(e) => {
                // HTML `maxLength` 대신 판정 쪽에서 자른다 — 속성은 astral 문자를 반으로 쪼갠다.
                setUserQuery(boundMemberSearchInput(e.target.value));
                setSelectedUser(null);
              }}
              prefix="🔍"
            />
            {/* idle 부터 **항상 있는** live region — 노드가 생겼다 없어지면 알림이 안 간다. */}
            <div
              data-sr-live
              role="status"
              aria-live="polite"
              aria-atomic="true"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                whiteSpace: "nowrap",
              }}
            >
              {userSearchNotice}
            </div>
            {userSearchNotice ? (
              <div className="naver-order-page__empty">{userSearchNotice}</div>
            ) : userSearchError ? (
              <div className="naver-order-page__empty" style={{ color: "#EF4444" }} role="alert">
                회원 검색에 실패했습니다 — {userSearchError}
              </div>
            ) : usersLoading ? (
              <div className="naver-order-page__empty">회원 목록 불러오는 중...</div>
            ) : userQuery.trim() === "" ? (
              <div className="naver-order-page__empty">검색어를 입력하면 후보가 표시됩니다.</div>
            ) : candidates.length === 0 ? (
              <div className="naver-order-page__empty">일치하는 회원이 없습니다.</div>
            ) : (
              <div className="naver-order-page__candidates">
                {candidates.map((u) => (
                  <div
                    key={u.id}
                    className={`naver-order-page__candidate${selectedUser?.id === u.id ? " naver-order-page__candidate--selected" : ""}`}
                    onClick={() => setSelectedUser(u)}
                  >
                    <span>
                      {u.name} <span className="naver-order-page__muted">{u.user_id}</span>
                    </span>
                    <span className="naver-order-page__muted">{u.hp_masked || "-"}</span>
                  </div>
                ))}
              </div>
            )}
            {selectedUser && (
              <div className="naver-order-page__selected">
                선택됨: <strong>{selectedUser.name}</strong> ({selectedUser.user_id})
              </div>
            )}
          </section>

          <section className="naver-order-page__section">
            <div className="naver-order-page__section-title">상품</div>
            <div className="naver-order-page__row">
              <Select
                label="상품 유형"
                value={form.product_type}
                onChange={(e) => {
                  updateField("product_type", e.target.value as NaverOrderProductType);
                  // 유형 변경 시 검색어/결과/선택 초기화 (type 파라미터가 바뀌므로 재검색)
                  resetProductSearch();
                }}
                options={PRODUCT_TYPE_OPTIONS}
              />
              <Input
                label={`${PRODUCT_LABEL[form.product_type]} 검색 (이름 / 브랜드)`}
                placeholder="검색어를 입력하세요"
                value={productQuery}
                onChange={(e) => {
                  setProductQuery(e.target.value);
                  setSelectedProduct(null);
                }}
                prefix="🔍"
              />
              <Input
                label="수량"
                type="number"
                min={1}
                value={form.quantity}
                onChange={(e) => updateField("quantity", e.target.value)}
              />
            </div>

            {productQuery.trim().length < 2 ? (
              <div className="naver-order-page__empty">
                검색어를 2자 이상 입력하면 후보가 표시됩니다.
              </div>
            ) : productLoading ? (
              <div className="naver-order-page__empty">검색 중...</div>
            ) : productSearched && productResults.length === 0 ? (
              <div className="naver-order-page__empty">일치하는 상품이 없습니다.</div>
            ) : productResults.length > 0 ? (
              <div className="naver-order-page__candidates">
                {productResults.map((p) => (
                  <div
                    key={p.id}
                    className={`naver-order-page__candidate${selectedProduct?.id === p.id ? " naver-order-page__candidate--selected" : ""}`}
                    onClick={() => setSelectedProduct(p)}
                  >
                    <span>
                      {p.name}{" "}
                      <span className="naver-order-page__muted">
                        {p.brand ?? p.type_display} · #{p.id}
                      </span>
                    </span>
                    <span className="naver-order-page__muted">
                      {(p.discount_price ?? p.price).toLocaleString()}원
                    </span>
                  </div>
                ))}
              </div>
            ) : null}

            {selectedProduct && (
              <div className="naver-order-page__selected">
                선택됨: <strong>{selectedProduct.name}</strong> (#{selectedProduct.id})
              </div>
            )}
          </section>

          <section className="naver-order-page__section">
            <div className="naver-order-page__section-title">결제 정보</div>
            <div className="naver-order-page__row">
              <Input
                label="결제 금액 (원)"
                type="number"
                min={1}
                placeholder="예: 30000"
                value={form.total_amount}
                onChange={(e) => updateField("total_amount", e.target.value)}
              />
              <Input
                label="네이버 주문번호 (선택)"
                placeholder="비우면 자동 생성"
                value={form.naver_order_id}
                onChange={(e) => updateField("naver_order_id", e.target.value)}
              />
            </div>
            <div className="naver-order-page__row">
              <div className="naver-order-page__field">
                <label className="naver-order-page__label">결제 일시 (선택)</label>
                <input
                  type="datetime-local"
                  className="naver-order-page__input"
                  value={form.payment_date}
                  onChange={(e) => updateField("payment_date", e.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="naver-order-page__section">
            <div className="naver-order-page__section-title">메모 (선택)</div>
            <textarea
              className="naver-order-page__textarea"
              rows={4}
              placeholder="네이버 주문 관련 메모"
              value={form.note}
              onChange={(e) => updateField("note", e.target.value)}
            />
          </section>

          {error && <div className="naver-order-page__error">{error}</div>}

          <div className="naver-order-page__actions">
            <Button variant="outline" onClick={handleReset} disabled={submitting}>
              초기화
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "등록 중..." : "등록"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NaverOrderRegister;
